// BlueLine design-system components (ported from the BlueLine Design System bundle).
// Class names match src/styles/components.css; colours come from src/styles/tokens.css.
import { forwardRef, useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

const PATHS = {
  check: 'M4 12.5l5 5L20 6.5',
  x: 'M6 6l12 12M18 6L6 18',
  arrow: 'M5 4l10 8-10 8z',
  chevronRight: 'M9 5l7 7-7 7',
  chevronLeft: 'M15 5l-7 7 7 7',
  chevronDown: 'M5 9l7 7 7-7',
  lock: 'M7 11V8a5 5 0 0110 0v3M5 11h14v10H5z',
  play: 'M8 5v14l11-7z',
  clock: 'M12 7v5l3 2M12 21a9 9 0 110-18 9 9 0 010 18z',
  award: 'M12 15a6 6 0 100-12 6 6 0 000 12zM8.5 14L7 22l5-3 5 3-1.5-8',
  alert: 'M12 9v4M12 17h.01M10.3 3.9L2 18a2 2 0 001.7 3h16.6a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z',
  info: 'M12 11v6M12 7h.01M12 21a9 9 0 110-18 9 9 0 010 18z',
  bulb: 'M9 18h6M10 21h4M12 3a6 6 0 00-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0012 3z',
  file: 'M14 3H6v18h12V7zM14 3v4h4',
  download: 'M12 4v11M7 10l5 5 5-5M5 20h14',
  book: 'M4 5a2 2 0 012-2h13v16H6a2 2 0 00-2 2zM4 21V5',
} as const;
export type IconName = keyof typeof PATHS;

export function Icon({ name, label, strokeWidth = 2, className }: { name: IconName; label?: string; strokeWidth?: number; className?: string }) {
  const filled = name === 'arrow' || name === 'play';
  return (
    <svg className={cx('bl-icon', className)} viewBox="0 0 24 24" aria-hidden={label ? undefined : true} role={label ? 'img' : undefined}
      aria-label={label} fill={filled ? 'currentColor' : 'none'} stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={PATHS[name]} />
    </svg>
  );
}

type Variant = 'primary' | 'enroll' | 'secondary' | 'ghost' | 'danger';
type ButtonOwnProps = { variant?: Variant; size?: 'sm' | 'md' | 'lg'; block?: boolean; icon?: IconName; iconAfter?: IconName; busy?: boolean };

export const Button = forwardRef<HTMLButtonElement, ButtonOwnProps & ButtonHTMLAttributes<HTMLButtonElement>>(function Button(
  { variant = 'primary', size = 'md', block, icon, iconAfter, busy, className, children, type = 'button', disabled, ...rest }, ref) {
  return (
    <button ref={ref} type={type} disabled={disabled || busy} aria-busy={busy || undefined}
      className={cx('bl-btn', `bl-btn-${variant}`, size !== 'md' && `bl-btn-${size}`, block && 'bl-btn-block', className)} {...rest}>
      {icon && <Icon name={icon} />}
      {busy ? 'Working…' : children}
      {iconAfter && <Icon name={iconAfter} />}
    </button>
  );
});

export function ButtonLink({ to, variant = 'primary', size = 'md', icon, iconAfter, children, className }:
  ButtonOwnProps & { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={cx('bl-btn', `bl-btn-${variant}`, size !== 'md' && `bl-btn-${size}`, className)}>
      {icon && <Icon name={icon} />}
      {children}
      {iconAfter && <Icon name={iconAfter} />}
    </Link>
  );
}

export type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'solid';
export function Badge({ tone = 'neutral', icon, children }: { tone?: Tone; icon?: IconName; children: ReactNode }) {
  const i = icon ?? (tone === 'success' ? 'check' : tone === 'danger' ? 'x' : undefined);
  return <span className={cx('bl-badge', `bl-badge-${tone}`)}>{i && <Icon name={i} />}{children}</span>;
}

export function SectionTitle({ level = 1, overline, subtitle, children, rule = true }:
  { level?: 1 | 2; overline?: ReactNode; subtitle?: ReactNode; children: ReactNode; rule?: boolean }) {
  const H = level === 1 ? 'h1' : 'h2';
  return (
    <header className={cx('bl-title', level > 1 && 'bl-title-h2')}>
      {overline && <span className="bl-title-overline">{overline}</span>}
      <H className="bl-title-heading">{children}</H>
      {rule && <hr className="bl-title-rule" aria-hidden="true" />}
      {subtitle && <p className="bl-title-sub">{subtitle}</p>}
    </header>
  );
}

export function Panel({ title, tone = 'brand', children }: { title?: ReactNode; tone?: 'brand' | 'accent'; children: ReactNode }) {
  return (
    <section className={cx('bl-panel', tone === 'accent' && 'bl-panel-accent')}>
      {title && <h3 className="bl-panel-head">{title}</h3>}
      <div className="bl-panel-body">{children}</div>
    </section>
  );
}

export function MarkerList({ marker = 'arrow', items }: { marker?: 'arrow' | 'check' | 'cross'; items: ReactNode[] }) {
  const icon: IconName = marker === 'check' ? 'check' : marker === 'cross' ? 'x' : 'arrow';
  return (
    <ul className={cx('bl-list', `bl-list-${marker}`)}>
      {items.map((it, i) => <li key={i}><Icon name={icon} strokeWidth={2.5} /><span>{it}</span></li>)}
    </ul>
  );
}

const CALLOUT_ICON: Record<string, IconName> = { implication: 'bulb', important: 'alert', note: 'info', banner: 'arrow', error: 'alert' };
export function Callout({ variant = 'note', title, children, role }:
  { variant?: 'implication' | 'important' | 'note' | 'banner' | 'error'; title?: string; children: ReactNode; role?: string }) {
  return (
    <div className={cx('bl-callout', `bl-callout-${variant}`)} role={role ?? (variant === 'error' ? 'alert' : undefined)}>
      <Icon name={CALLOUT_ICON[variant]} />
      <div>{title && <span className="bl-callout-title">{title} </span>}{children}</div>
    </div>
  );
}

export function ProgressBar({ value, label, showValue = true, size = 'md' }: { value: number; label?: string; showValue?: boolean; size?: 'sm' | 'md' }) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div className={cx('bl-progress', pct >= 100 && 'bl-progress-done', size === 'sm' && 'bl-progress-sm')}>
      {(label || showValue) && (
        <div className="bl-progress-top"><span>{label ?? ''}</span>{showValue && <strong>{pct}%</strong>}</div>
      )}
      <div className="bl-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={label ?? 'Progress'}>
        <div className="bl-progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function StatTile({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="bl-stat">
      <span className="bl-stat-label">{label}</span>
      <span className="bl-stat-value">{value}</span>
      {hint && <span className="bl-stat-hint">{hint}</span>}
    </div>
  );
}

type FieldProps = { label: string; help?: string; error?: string | null; multiline?: false } & InputHTMLAttributes<HTMLInputElement>;
type AreaProps = { label: string; help?: string; error?: string | null; multiline: true } & TextareaHTMLAttributes<HTMLTextAreaElement>;
export function TextField(props: FieldProps | AreaProps) {
  const auto = useId();
  const { label, help, error, multiline, id, className, required, ...rest } = props;
  const fid = id ?? auto;
  const describedBy = error || help ? `${fid}-h` : undefined;
  return (
    <div className={cx('bl-field', error && 'bl-field-error', className)}>
      <label className="bl-field-label" htmlFor={fid}>{label}{required && <span className="bl-req" aria-hidden="true">*</span>}</label>
      {multiline
        ? <textarea id={fid} className="bl-input" aria-invalid={error ? true : undefined} aria-describedby={describedBy} required={required} {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)} />
        : <input id={fid} className="bl-input" aria-invalid={error ? true : undefined} aria-describedby={describedBy} required={required} {...(rest as InputHTMLAttributes<HTMLInputElement>)} />}
      {error ? <span className="bl-field-help" id={`${fid}-h`}><Icon name="alert" />{error}</span>
        : help ? <span className="bl-field-help" id={`${fid}-h`}>{help}</span> : null}
    </div>
  );
}

export function Lockup({ size = 40, markOnly, tagline, to = '/' }: { size?: number; markOnly?: boolean; tagline?: string; to?: string }) {
  return (
    <Link to={to} className="bl-lockup" aria-label="BlueLine Environmental Institute home">
      <img src="/blueline-seal.png" width={size} height={size} alt="" />
      {!markOnly && (
        <span className="bl-lockup-text">
          <span className="bl-lockup-name"><b>BlueLine</b> Environmental Institute</span>
          {tagline && <span className="bl-lockup-tag">{tagline}</span>}
        </span>
      )}
    </Link>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return <p className="app-loading" role="status">{label}</p>;
}
